// Shared visual constants — Fall Guys-style palette + scene tuning. Imported
// by Scene, decor modules, and HUD CSS via the corresponding variables in
// design.css. Keep this list tight: every entry should be referenced from
// multiple places, otherwise it's just clutter.

export const VISUAL_THEME = {
  // Palette
  safe: 0x4cc9ff,        // bright cyan — safe platforms / ground rails
  safeTrim: 0xffd166,    // gold trim around safe surfaces
  danger: 0xff3b8a,      // pink/red — moving danger (spinners, blockers)
  question: 0x9b5cf6,    // purple — word gates
  questionGlow: 0x35d977,// green — correct-answer glow on word gates
  boost: 0xff8b3d,       // orange — speed-up pickups
  reward: 0xffd166,      // gold — coins, stars
  skyTop: 0x9ce0ff,
  skyHorizon: 0xfff6c2,
} as const;

export const SCENE_TUNING = {
  // Lighting — boosted for Fall Guys cartoon brightness.
  toneMappingExposure: 1.45,
  hemiBoost: 1.4,         // multiplier on quality.hemiLightIntensity
  ambientFloor: 0.55,     // floor on theme.ambient (kids never see pitch black)
  sunIntensityMultiplier: 1.15,
} as const;

/** Build a soft circular shadow blob — cheap stand-in for real shadows under
 *  the player + ghosts. Renders as a flat disc just above the floor with a
 *  radial-gradient texture (alpha fade from center → edge). One texture is
 *  cached and reused so every blob shares a draw call's worth of work. */
import * as THREE from "three";
let _blobTexture: THREE.CanvasTexture | null = null;
function blobTexture(): THREE.CanvasTexture {
  if (_blobTexture) return _blobTexture;
  const size = 128;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  const grad = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  grad.addColorStop(0, "rgba(0,0,0,0.55)");
  grad.addColorStop(0.55, "rgba(0,0,0,0.32)");
  grad.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
  _blobTexture = new THREE.CanvasTexture(canvas);
  _blobTexture.colorSpace = THREE.SRGBColorSpace;
  _blobTexture.anisotropy = 1;
  return _blobTexture;
}

/** Create a blob-shadow mesh ready to add under a character group. Caller is
 *  responsible for positioning it just above the walkable surface each frame
 *  (or anchoring it under the character if the character group already sits
 *  at the surface). */
export function createBlobShadow(radius = 0.6): THREE.Mesh {
  const geom = new THREE.PlaneGeometry(radius * 2, radius * 2);
  geom.rotateX(-Math.PI / 2);
  const mat = new THREE.MeshBasicMaterial({
    map: blobTexture(),
    transparent: true,
    depthWrite: false,
    opacity: 0.9,
    fog: false,
  });
  const mesh = new THREE.Mesh(geom, mat);
  // Render below other characters but above the floor — small renderOrder
  // bump avoids z-fighting on the same plane as the path tile.
  mesh.renderOrder = 1;
  return mesh;
}

export const CAMERA_TUNING = {
  // Slightly closer + lower than the previous (5.4, 2.65). Keeps the next
  // obstacle visible without the camera feeling far away.
  baseBack: 5.0,
  speedBackBoost: 1.0,    // total back at full speed = baseBack + speedBackBoost
  baseHeight: 1.95,
  lookAhead: 2.4,
  lookHeightOffset: 0.95,
  lerpRate: 22,           // higher = snappier follow
} as const;
